// linkify.ts — the ONE URL matcher for post bodies (1.24.00 C1).
//
// Two renderers read a post body and must agree, character for character, on
// where a link starts and ends:
//   • PostBody.tsx  — renders each link as a real <a> (the visible feed card).
//   • bodyText.ts   — the plain-text twin used by the ReportModal preview.
//
// If those two ever grew their own regexes, a reporter could be looking at a
// different URL than the reader is — which is exactly the kind of quiet
// divergence the report flow must not have. So both call `splitOnLinks` and
// neither owns a URL pattern of its own.
//
// Deliberately conservative:
//   • http/https only. No bare `example.com`, no `www.` — guessing a scheme
//     for text the author never wrote as a link is how you turn a sentence
//     into a wrong link.
//   • `<` and `>` terminate a URL, so a URL can never swallow the legacy
//     markup (`<br/>`, `<a class="tag">`) that seed bodies still carry.
//   • Trailing sentence punctuation is trimmed off the href — "see
//     https://example.com." links to the page, not to `…com.`.

/**
 * Absolute http(s) URLs. Angle brackets, quotes and whitespace all terminate a
 * match. Global + case-insensitive; callers must reset `lastIndex` (splitOnLinks
 * does).
 */
export const URL_RE = /https?:\/\/[^\s<>"']+/gi;

/** Sentence punctuation that is never part of a URL when it lands at the end. */
const TRAILING_PUNCTUATION = `.,;:!?'"`;

/** Closing bracket → its opener, for the "only trim an UNMATCHED closer" rule. */
const CLOSERS: Record<string, string> = { ')': '(', ']': '[', '}': '{' };

function countOf(text: string, ch: string): number {
  let n = 0;
  for (const c of text) if (c === ch) n += 1;
  return n;
}

/**
 * Trim punctuation a sentence put after a URL, without breaking URLs that
 * legitimately end in a bracket (Wikipedia's `..._(disambiguation)`): a closing
 * bracket is only dropped when the URL has no matching opener for it.
 */
export function trimUrlTail(url: string): string {
  let out = url;
  for (;;) {
    const last = out[out.length - 1];
    if (!last) return out;
    if (TRAILING_PUNCTUATION.includes(last)) {
      out = out.slice(0, -1);
      continue;
    }
    const opener = CLOSERS[last];
    if (opener && countOf(out, last) > countOf(out, opener)) {
      out = out.slice(0, -1);
      continue;
    }
    return out;
  }
}

/** One run of a body: plain text, or (when `href` is set) a link. */
export interface LinkSegment {
  /** The exact source text of this run. For a link run this IS the href. */
  text: string;
  /** Present only on a link run. */
  href?: string;
}

/**
 * Split raw body text into alternating plain-text and link runs. A link run's
 * `text` is identical to its `href`, so a caller can render the URL verbatim
 * (PostBody) or keep it untouched in plain text (bodyText) from one tokenizer.
 */
export function splitOnLinks(text: string): LinkSegment[] {
  const segments: LinkSegment[] = [];
  let last = 0;
  URL_RE.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = URL_RE.exec(text)) !== null) {
    const href = trimUrlTail(match[0]);
    // `https://` with nothing after the punctuation trim isn't a link — leave
    // it in the plain-text run rather than emitting an anchor to nowhere.
    if (href.length <= match[0].indexOf('//') + 2) {
      URL_RE.lastIndex = match.index + match[0].length;
      continue;
    }
    if (match.index > last) segments.push({ text: text.slice(last, match.index) });
    segments.push({ text: href, href });
    last = match.index + href.length;
    URL_RE.lastIndex = last;
  }

  if (last < text.length) segments.push({ text: text.slice(last) });
  return segments;
}
